import type {
  AcknowledgeAllNotificationsHandler,
  AcknowledgeNotificationHandler,
  ConnectEmailAccountHandler,
  ConnectedEmailAccountDetailsHandler,
  ConnectedEmailAccountStatusHanlder,
  CreatedIndeedJobSeekerKeyHandler,
  DeleteFollowUpHandler,
  DisconnectEmailAccountHandler,
  FallbackSaveMessagesHandler,
  FetchMostRecentSyncedIndeedMessageHandler,
  GetCalendarSchedulingLinkHandler,
  GetIndeedJobSeekerKeyHandler,
  GetLastCandidateNotificationHandler,
  GetNotificationsHandler,
  GetReachoutConversionHandler,
  GetSentMessagesToCandidateHandler,
  NylasCalendarRequestWrapperHandler,
  SaveIndeedMessagesNotificationsHandler,
  SaveLinkedinMessagesNotificationsHandler,
  SaveLinkedinRecruiterMessagesNotificationsHandler,
  SendPushNotificationHandler,
} from './NotificationsClient.types';

import { BaseClient } from 'clients/BaseClient/BaseClient';
import { hosts } from 'config';
import { replaceUrlToLocal } from 'helpers/replaceUrlToLocal';

export class NotificationsClient extends BaseClient {
  getNotifications: GetNotificationsHandler = async (filters = { limit: 100 }) => {
    const response: Awaited<ReturnType<GetNotificationsHandler>> = await this.request({
      url: `${hosts.notificationsApi()}/notification/`,
      method: 'GET',
      params: { ...filters },
    });

    return {
      ...response,
      data: {
        ...response.data,
        results: response.data.results.map((notification) => ({
          ...notification,
          url: replaceUrlToLocal(notification.url, { debug: true }),
        })),
      },
    };
  };

  acknowledgeNotification: AcknowledgeNotificationHandler = (id) => {
    return this.request({
      url: `${hosts.notificationsApi()}/notification/${id}/`,
      method: 'PATCH',
      data: { status: 'acknowledged' },
    });
  };

  acknowledgeAllNotification: AcknowledgeAllNotificationsHandler = () => {
    return this.request({
      url: `${hosts.notificationsApi()}/notification/read_all/`,
      method: 'PATCH',
    });
  };

  getLastCandidateNotification: GetLastCandidateNotificationHandler = ({ send_method }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/notifications/candidates/${send_method}/last`,
      method: 'GET',
    });
  };

  getSentMessageToCandidate: GetSentMessagesToCandidateHandler = ({ candidate_id }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/notifications/candidates/${candidate_id}`,
      method: 'GET',
    });
  };

  sendPushNotification: SendPushNotificationHandler = (data) => {
    return this.request({
      url: `${hosts.notificationsApi()}/expo/push_notification/`,
      method: 'POST',
      data,
    });
  };

  connectEmailAccount: ConnectEmailAccountHandler = () => {
    return this.request({
      url: `${hosts.notificationsApi()}/email/oauth/url`,
      method: 'GET',
    });
  };

  connectedEmailAccountStatus: ConnectedEmailAccountStatusHanlder = () => {
    return this.request({
      url: `${hosts.notificationsApi()}/email/oauth/status`,
      method: 'GET',
    });
  };

  disconnectEmailAccount: DisconnectEmailAccountHandler = () => {
    return this.request({
      url: `${hosts.notificationsApi()}/email/oauth/disconnect/`,
      method: 'POST',
    });
  };

  connectedAccountDetails: ConnectedEmailAccountDetailsHandler = () => {
    return this.request({
      url: `${hosts.notificationsApi()}/email/oauth/details/`,
      method: 'GET',
    });
  };

  saveLinkedinRecruiterMessagesNotifications: SaveLinkedinRecruiterMessagesNotificationsHandler = ({ messages }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/linkedin_recruiter/messages_received/`,
      method: 'POST',
      data: {
        messages,
      },
    });
  };

  saveLinkedinMessagesNotifications: SaveLinkedinMessagesNotificationsHandler = ({ messages }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/linkedin/messages_received/`,
      method: 'POST',
      data: {
        messages,
      },
    });
  };

  saveIndeedMessagesNotifications: SaveIndeedMessagesNotificationsHandler = ({ messages }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/indeed/messages_received/`,
      method: 'POST',
      data: {
        messages,
      },
    });
  };

  fetchIndeedJobSeekerKey: GetIndeedJobSeekerKeyHandler = ({ tenantId, candidateId }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/indeed_message_pulling/job_seeker_key/fetch/`,
      method: 'POST',
      data: {
        tenant_id: tenantId,
        candidate_id: candidateId,
      },
    });
  };

  createdIndeedJobSeekerKey: CreatedIndeedJobSeekerKeyHandler = ({
    credentialUsername,
    jobSeekerKey,
    candidateId,
    tenantId,
  }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/indeed_message_pulling/job_seeker_key/upsert/`,
      method: 'POST',
      data: {
        credential_username: credentialUsername,
        job_seeker_key: jobSeekerKey,
        candidate_id: candidateId,
        tenant_id: tenantId,
      },
    });
  };

  fetchMostRecentSyncedIndeedMessage: FetchMostRecentSyncedIndeedMessageHandler = ({
    credentialUsername,
    candidateId,
    platform,
  }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/indeed_message_pulling/last_message/`,
      method: 'POST',
      data: {
        credential_username: credentialUsername,
        candidate_id: candidateId,
        platform: platform,
      },
    });
  };

  fallbackSaveMessages: FallbackSaveMessagesHandler = ({
    candidateId,
    credentialUsername,
    platform,
    messages,
    candidateName,
  }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/message_received/mongo_fallback/`,
      method: 'POST',
      data: {
        candidate_id: candidateId,
        credential_username: credentialUsername,
        platform,
        messages,
        candidate_name: candidateName,
      },
    });
  };

  deleteFollowUp: DeleteFollowUpHandler = ({ activityLogId }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/follow_up/${activityLogId}`,
      method: 'DELETE',
    });
  };

  getReachoutConversionSummary: GetReachoutConversionHandler = ({
    start_date,
    end_date,
    users = [],
    campaigns = [],
  }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/conversations_summary/`,
      method: 'POST',
      data: {
        start_date,
        end_date,
        user_ids: users,
        campaign_ids: campaigns,
      },
    });
  };

  getCalendarSchedulingLink: GetCalendarSchedulingLinkHandler = (context, schedulerType, options) => {
    return this.request({
      url: `${hosts.notificationsApi()}/calendar/scheduling-link/`,
      method: 'POST',
      data: {
        ...(context ? { campaign_context: context } : {}),
        scheduler_type: schedulerType,
        ...(options?.autoFillJobContext !== undefined ? { auto_fill_job_context: options.autoFillJobContext } : {}),
      },
    });
  };

  nylasCalendarRequestWrapper: NylasCalendarRequestWrapperHandler = ({ method, path, body, query_params }) => {
    return this.request({
      url: `${hosts.notificationsApi()}/calendar/nylas/requests/`,
      method: 'POST',
      data: {
        method,
        path,
        body,
        query_params,
      },
    });
  };
}

export const notificationsClient = new NotificationsClient();
